import PortalDocument from "./PortalDocument";

import {DocumentFile, MetadataPdfa, Parameter} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../shared/helper/FunctionHelper";

export interface DocumentFilterInterface {
	filteredExtensions: Array<string>;
	pdfaRequired: boolean;
	passwordRequired: boolean;
	signatureRequired: boolean;
	attachmentsRequired: boolean;
	searchValue: string;
}

export default class DocumentFilter implements DocumentFilterInterface, Parameter {
	public filteredExtensions: Array<string>;
	public pdfaRequired: boolean;
	public passwordRequired: boolean;
	public signatureRequired: boolean;
	public attachmentsRequired: boolean;
	public searchValue: string;

	constructor(data?: any) {
		this.filteredExtensions = FunctionHelper.exists(data?.filteredExtensions) ? data.filteredExtensions : [];
		this.pdfaRequired = FunctionHelper.exists(data?.pdfaRequired) ? data.pdfaRequired : false;
		this.passwordRequired = FunctionHelper.exists(data?.passwordRequired) ? data.passwordRequired : false;
		this.signatureRequired = FunctionHelper.exists(data?.signatureRequired) ? data.signatureRequired : false;
		this.attachmentsRequired = FunctionHelper.exists(data?.attachmentsRequired) ? data.attachmentsRequired : false;
		this.searchValue = FunctionHelper.exists(data?.searchValue) ? data.searchValue : "";
	}

	/**
	 * checks if a document is filtered by the current filters
	 * @private
	 */
	public filter(portalDocument: PortalDocument): boolean {
		let documentInfo: DocumentFile | undefined = portalDocument.getDocumentFile();
		let filtered: boolean = false;

		for (let extension of this.filteredExtensions) {
			if (extension === documentInfo?.fileExtension!.toUpperCase()) {
				filtered = true;
				break;
			}
		}

		let hasPassword: boolean = portalDocument.hasPasswordProtection() || portalDocument.hasCertificateProtection();
		if (this.passwordRequired && !hasPassword) {
			filtered = true;
		}

		let hasSignature: boolean = portalDocument.hasSignature();
		if (this.signatureRequired && !hasSignature) {
			filtered = true;
		}

		let hasAttachments: boolean = portalDocument.getAttachments().length > 0 && !portalDocument.isPortfolio();
		if (this.attachmentsRequired && !hasAttachments) {
			filtered = true;
		}

		let isArchive: boolean = FunctionHelper.applyValue(
			documentInfo?.metadata?.information?.pdfa?.part, MetadataPdfa.getPartDefault()
		) !== '';
		if (this.pdfaRequired && !isArchive) {
			filtered = true;
		}

		if (this.searchValue !== "" &&
			portalDocument.getFileName().toUpperCase().indexOf(this.searchValue.toUpperCase()) === -1) {
			filtered = true;
		}

		return filtered;
	}

	/**
	 * checks if any filter is active
	 */
	public isFiltered(): boolean {
		return (
			this.pdfaRequired ||
			this.passwordRequired ||
			this.signatureRequired ||
			this.attachmentsRequired ||
			this.filteredExtensions.length > 0
		);
	}

	/**
	 * Resets all filter
	 */
	public reset(): void {
		this.filteredExtensions = [];
		this.attachmentsRequired = false;
		this.signatureRequired = false;
		this.passwordRequired = false;
		this.pdfaRequired = false;
	}

	public static fromJson(data: any): DocumentFilter {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new DocumentFilter(data);
	}

	public toJson(): any {
		return {
			'filteredExtensions': this.filteredExtensions,
			'pdfaRequired': this.pdfaRequired,
			'passwordRequired': this.passwordRequired,
			'signatureRequired': this.signatureRequired,
			'attachmentsRequired': this.attachmentsRequired,
			'searchValue': this.searchValue
		};
	}

	public clone(): DocumentFilter {
		return DocumentFilter.fromJson(this.toJson());
	}
}